import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { AtSign, BookOpen, Boxes, History, Upload } from "lucide-react";
import { CapabilityForm } from "@/components/CapabilityForm";
import { ResultGrid } from "@/components/ResultGrid";
import { Dialog } from "@/components/ui/dialog";
import {
  libraryItemsPath,
  productionAssetsPath,
  swrFetcher,
  type CapabilityInfo,
  type JobInfo,
  type LibraryItemsResponse,
  type ProductionAssetInfo,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { bindReference, referenceBindings } from "@/lib/mentions";

export type AssetPickerTab = "elements" | "library" | "recent" | "uploads";

const TABS = [
  { key: "elements" as const, label: "Elements", icon: Boxes },
  { key: "library" as const, label: "Library", icon: BookOpen },
  { key: "recent" as const, label: "Recent", icon: History },
  { key: "uploads" as const, label: "Uploads", icon: Upload },
];

export function StudioAssetPicker({
  open,
  initialTab = "elements",
  onClose,
  capability,
  modelKey,
  params,
  onParamChange,
  jobs,
  onContinue,
  onUseLibrary,
  onCancelled,
}: {
  open: boolean;
  initialTab?: AssetPickerTab;
  onClose: () => void;
  capability: CapabilityInfo | null;
  modelKey: string;
  params: Record<string, unknown>;
  onParamChange: (name: string, value: unknown) => void;
  jobs: JobInfo[];
  onContinue: (job: JobInfo) => void;
  onUseLibrary: (id: string) => void;
  onCancelled: () => void;
}) {
  const [tab, setTab] = useState<AssetPickerTab>(initialTab);
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [initialTab, open]);

  const { data: elements } = useSWR<ProductionAssetInfo[]>(
    open ? productionAssetsPath(undefined, undefined, false) : null,
    swrFetcher
  );
  const { data: library } = useSWR<LibraryItemsResponse>(
    open ? libraryItemsPath({ limit: 18, sort: "newest" }) : null,
    swrFetcher
  );
  const uploadNames = useMemo(
    () => capability?.ui_schema.params.filter((param) => param.widget === "upload" || param.widget === "multi_upload").map((param) => param.name) ?? [],
    [capability]
  );

  const insertElement = (asset: ProductionAssetInfo) => {
    const binding = bindReference(params, { token: `@${asset.slug}`, kind: "element", id: asset.id, updated_at: asset.updated_at });
    const current = typeof params.prompt === "string" ? params.prompt : "";
    const space = current && !/\s$/.test(current) ? " " : "";
    onParamChange("reference_bindings", [...referenceBindings(params).filter((item) => item.token !== binding.token), binding]);
    onParamChange("prompt", `${current}${space}${binding.token}`);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} title="Add production asset" className="sm:max-w-3xl">
      <div className="flex max-w-full gap-1 overflow-x-auto border-b border-studio-border px-3 sm:px-5" role="tablist" aria-label="Asset sources">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              onClick={() => setTab(item.key)}
              className={cn(
                "-mb-px inline-flex min-h-12 shrink-0 items-center gap-2 border-b-2 px-3 text-xs font-semibold transition-colors",
                tab === item.key ? "border-studio-accent text-studio-ink" : "border-transparent text-studio-muted hover:text-studio-ink"
              )}
            >
              <Icon className="h-3.5 w-3.5" /> {item.label}
            </button>
          );
        })}
      </div>

      <div className="min-h-72 flex-1 overflow-y-auto p-4 sm:p-5">
        {tab === "elements" && (
          <div>
            <p className="mb-3 text-xs leading-5 text-studio-muted">Mention a saved character, location, prop or wardrobe item. Its reference images are checked and included in the cost review.</p>
            {(elements ?? []).length > 0 ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {(elements ?? []).map((asset) => (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => insertElement(asset)}
                    className="flex min-h-20 items-start gap-3 rounded-xl border border-studio-border bg-studio-card p-3 text-left transition-colors hover:border-studio-accent/35 hover:bg-studio-card-hover"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-studio-accent-soft text-studio-accent"><AtSign className="h-4 w-4" /></span>
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-semibold text-studio-ink">{asset.name}</span>
                      <span className="mt-0.5 block font-mono text-[10px] text-studio-accent">@{asset.slug}</span>
                      <span className="mt-1 block line-clamp-1 text-[10px] text-studio-muted">{asset.summary || asset.type_label || asset.type}</span>
                    </span>
                  </button>
                ))}
              </div>
            ) : <Empty message="No elements yet. Create reusable characters, locations and props from Elements." />}
          </div>
        )}

        {tab === "library" && (
          <div>
            <p className="mb-3 text-xs leading-5 text-studio-muted">Load a saved generation recipe into Studio as a draft. Nothing is submitted until you review cost.</p>
            {(library?.items ?? []).length > 0 ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {(library?.items ?? []).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onUseLibrary(item.id)}
                    className="rounded-xl border border-studio-border bg-studio-card p-3 text-left transition-colors hover:border-studio-accent/35 hover:bg-studio-card-hover"
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="truncate text-xs font-semibold text-studio-ink">{item.title}</span>
                      <span className="shrink-0 rounded-md bg-studio-panel px-1.5 py-0.5 text-[9px] uppercase text-studio-muted">{item.media_type}</span>
                    </span>
                    <span className="mt-1.5 line-clamp-2 text-[10px] leading-4 text-studio-muted">{item.description || item.prompt || "Saved production recipe"}</span>
                  </button>
                ))}
              </div>
            ) : <Empty message="Your reusable Library items will appear here." />}
          </div>
        )}

        {tab === "recent" && (
          <ResultGrid jobs={jobs.slice(0, 8)} variant="compact" onContinue={(job) => { onContinue(job); onClose(); }} onCancelled={onCancelled} />
        )}

        {tab === "uploads" && (
          uploadNames.length > 0 ? (
            <CapabilityForm
              modelKey={modelKey}
              capability={capability}
              params={params}
              onChange={onParamChange}
              includeParamNames={uploadNames}
              compact
            />
          ) : <Empty message="The selected workflow does not accept direct uploads. Choose Elements, Library, or a reference-capable model." />
        )}
      </div>
    </Dialog>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div className="flex min-h-56 items-center justify-center rounded-xl border border-dashed border-studio-border px-6 text-center text-xs leading-5 text-studio-muted">
      {message}
    </div>
  );
}
